import { useEffect, useState } from "react";
import Spacer from "../../components/Spacer";
import { apiUrl, executeRequest, getToken } from "../../functions/requests";
import List from "../../components/List";
import Button from "../../components/Button";
import SearchBar from "../../components/SearchBar";
import ListPagination from "../../components/ListPagination";
import FormInput from "../../components/FormInput";
import IconPlus from "../../icons/IconPlus";
import Message from "../../components/Message";
import Popup from "../../components/Popup";

export default function Customers() {
  const [loading, setLoading] = useState(true);

  // Message popup
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // List variables
  const [lastPage, setLastPage] = useState(null);
  const [page, setPage] = useState(1);
  const [customers, setCustomers] = useState([]);

  // Edit customer variables
  const [customerToEdit, setCustomerToEdit] = useState(null);

  // Add new customer variables
  const [addingNewCustomer, setAddingNewCustomer] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({});

  //Delete customer variables
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleteCustomerVerifyShow, setDeleteCustomerVerifyShow] =
    useState(false);

  // Search variables
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Show subscriptions for specific customer
  const [showCustomerSubscriptions, setShowCustomerSubscriptions] =
    useState(null);

  // Add new customer subscription
  const [addingNewCustomersSubscription, setAddingNewCustomerSubscription] =
    useState(null);
  const [newCustomerSubscriptionData, setNewCustomerSubscriptionData] =
    useState({});

  // New customer subscription dropdowns data
  const [
    newCustomerSubscriptionDropdownData,
    setNewCustomerSubscriptionDropdownData,
  ] = useState(null);

  // Show messages for each action or notification
  const messageClose = () => {
    setMessage("");
    setMessageType("");
  };

  // Set up customers data for list
  const setupCustomersData = async (data) => {
    setPage(data.current_page);
    setLastPage(data.last_page);
    setCustomers(data.data);
    setAddingNewCustomer(false);
    setCustomerToDelete(null);
    setDeleteCustomerVerifyShow(false);
    setCustomerToEdit(null);
    setNewCustomerData({});
  };

  // Get paginated customers
  const getCustomers = async () => {
    setLoading(true);
    await executeRequest(
      `${apiUrl}customers?page=${page}`,
      "GET",
      null,
      {
        Authorization: "Bearer " + getToken(),
      },
      async (data) => {
        await setupCustomersData(data);
        console.log(getToken());
        console.log(data);
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  // Search customers by their fields
  const searchCustomers = async (query) => {
    // Minimum 3 characters required to search
    if (query.length < 3) {
      return;
    }
    await executeRequest(
      `${apiUrl}customers?query=${query}&page=${page}`,
      "GET",
      null,
      {
        Authorization: "Bearer " + getToken(),
      },
      async (data) => {
        await setupCustomersData(data);
      },
      (error) => {
        console.log(error);
      }
    );
  };

  // Add new customer
  const addNewCustomer = async () => {
    setLoading(true);
    const finalFields = new FormData();
    for (let key in newCustomerData) {
      if (newCustomerData.hasOwnProperty(key)) {
        // If the value is a File (e.g., image or other binary data)
        if (newCustomerData[key] instanceof File) {
          finalFields.append(key, newCustomerData[key]);
        } else {
          // Append other data as strings
          finalFields.append(key, newCustomerData[key]);
        }
      }
    }

    await executeRequest(
      `${apiUrl}customers`,
      "POST",
      finalFields,
      {
        Authorization: "Bearer " + getToken(),
        Accept: "application/json",
      },
      async (data) => {
        await setupCustomersData(data);
        setMessage("Added new customer successfully.");
        setMessageType("success");
      },
      (error) => {
        setMessage("Problem adding new customer.");
        setMessageType("error");
      }
    );
    setLoading(false);
  };

  // Update existing customer
  const updateCustomer = async (originalCustomer) => {
    setLoading(true);
    delete customerToEdit.created_at;
    delete customerToEdit.updated_at;
    delete originalCustomer.created_at;
    delete originalCustomer.updated_at;
    const finalFields = new FormData();
    for (let key in customerToEdit) {
      if (customerToEdit.hasOwnProperty(key)) {
        if (originalCustomer && originalCustomer.hasOwnProperty(key)) {
          if (customerToEdit[key] !== null) {
            // Compare values and add to finalFields if different
            if (
              JSON.stringify(customerToEdit[key]) !==
              JSON.stringify(originalCustomer[key])
            ) {
              finalFields.append(key, customerToEdit[key]);
            }
          }
        } else {
          if (
            customerToEdit[key] &&
            customerToEdit[key] !== null &&
            customerToEdit[key] !== ""
          ) {
            finalFields.append(key, customerToEdit[key]);
          }
        }
      }
    }

    // Log finalFields content for debugging
    for (let pair of finalFields.entries()) {
      console.log(pair[0] + ": " + pair[1]);
    }

    await executeRequest(
      `${apiUrl}customers/${originalCustomer.uuid}`,
      "POST",
      finalFields,
      {
        Authorization: "Bearer " + getToken(),
      },
      async (data) => {
        await setupCustomersData(data);
        setMessage("Updated customer successfully.");
        setMessageType("success");
      },
      (error) => {
        setMessage("Problem updating customer.");
        setMessageType("error");
        console.log(error);
      }
    );
    setLoading(false);
  };

  // Show delete verify dialog
  const deleteCustomer = async (customer) => {
    setCustomerToDelete(customer);
    setDeleteCustomerVerifyShow(true);
  };

  // Cancel customer deletion
  const deleteCustomerCancel = () => {
    setCustomerToDelete(null);
    setDeleteCustomerVerifyShow(false);
  };

  // DELETE customer
  const deleteCustomerVerify = async () => {
    setLoading(true);
    await executeRequest(
      `${apiUrl}customers/${customerToDelete.uuid}`,
      "DELETE",
      null,
      {
        Authorization: "Bearer " + getToken(),
      },
      async (data) => {
        console.log(data);
        await setupCustomersData(data);
        setMessage("Deleted customer successfully.");
        setMessageType("success");
      },
      (error) => {
        setMessage("Problem deleting customer.");
        setMessageType("error");
        console.log(error);
      }
    );
    setLoading(false);
  };

  // Add new customer subscription
  const addNewCustomerSubscription = async () => {
    setLoading(true);
    const finalFields = new FormData();
    finalFields.append(
      "subscription_plan_uuid",
      newCustomerSubscriptionData.plan_uuid
    );
    finalFields.append("customer_uuid", addingNewCustomersSubscription.uuid);

    // Find the plan with the matching plan_uuid
    const selectedPlan =
      newCustomerSubscriptionDropdownData.subscription_plans.find(
        (plan) => plan.uuid === newCustomerSubscriptionData.plan_uuid
      );

    // Append the duration field if the plan is found
    if (selectedPlan) {
      finalFields.append("renew_timeframe", selectedPlan.duration);
    } else {
      console.error("Plan not found!");
    }

    await executeRequest(
      `${apiUrl}customer-subscriptions`,
      "POST",
      finalFields,
      {
        Authorization: "Bearer " + getToken(),
        Accept: "application/json",
      },
      async (data) => {
        await setupCustomersData(data);
        setAddingNewCustomerSubscription(null);
        setNewCustomerSubscriptionData({});
        setMessage("Added new customer subscription.");
        setMessageType("success");
      },
      (error) => {
        setMessage("Problem adding new customer subscription.");
        setMessageType("error");
        console.log(error);
      }
    );
    setLoading(false);
  };

  // Get customer subscription dropdown data for available packages
  const getCustomerSubscriptionDropdownData = async () => {
    await executeRequest(
      apiUrl +
        "customer-subscriptions/" +
        addingNewCustomersSubscription.uuid +
        "/dropdown-data",
      "GET",
      null,
      {
        Authorization: "Bearer " + getToken(),
      },
      (data) => {
        console.log(data);
        setNewCustomerSubscriptionDropdownData(data);
      },
      (error) => {
        console.log(error);
      }
    );
  };

  // Debounce effect to delay search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Search customers and return all results when search bar empties
  useEffect(() => {
    if (debouncedQuery) {
      searchCustomers(debouncedQuery);
    } else {
      getCustomers();
    }
  }, [debouncedQuery]);

  // Load dropdown data on add new subscription action
  useEffect(() => {
    if (addingNewCustomersSubscription !== null) {
      getCustomerSubscriptionDropdownData();
    }
  }, [addingNewCustomersSubscription]);

  // Get initial customers data
  useEffect(() => {
    getCustomers();
  }, []);

  if (
    addingNewCustomersSubscription !== null &&
    newCustomerSubscriptionDropdownData !== null
  ) {
    return (
      <>
        <h1 className="page-title">
          Add new subscription for {addingNewCustomersSubscription.first_name}{" "}
          {addingNewCustomersSubscription.last_name}
        </h1>
        <Button
          text="< Back to customers"
          type="underlined"
          onClicked={() => {
            setAddingNewCustomerSubscription(null);
            setNewCustomerSubscriptionData({});
          }}
        />
        <Spacer />
        <FormInput
          type="select"
          selectOptions={newCustomerSubscriptionDropdownData.applications}
          value={newCustomerSubscriptionData.application_uuid}
          showTitle={false}
          title="Application"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerSubscriptionData({
              ...newCustomerSubscriptionData,
              application_uuid: value,
            });
          }}
        />
        <Spacer />
        {newCustomerSubscriptionData.application_uuid && (
          <>
            <FormInput
              type="select"
              selectOptions={
                newCustomerSubscriptionData.application_uuid
                  ? newCustomerSubscriptionDropdownData.subscription_plans.filter(
                      (plan) =>
                        plan.application_uuid ===
                        newCustomerSubscriptionData.application_uuid
                    )
                  : []
              }
              value={newCustomerSubscriptionData.plan_uuid}
              showTitle={false}
              title="Plan"
              onChanged={(value) => {
                console.log(value);
                setNewCustomerSubscriptionData({
                  ...newCustomerSubscriptionData,
                  plan_uuid: value,
                });
              }}
            />
            <Spacer />
          </>
        )}
        <Button
          text="Add"
          icon={<IconPlus />}
          disabled={
            loading ||
            !newCustomerSubscriptionData.application_uuid ||
            !newCustomerSubscriptionData.plan_uuid
          }
          isLoading={loading}
          onClicked={() => addNewCustomerSubscription()}
        />
      </>
    );
  }

  if (showCustomerSubscriptions !== null) {
    return (
      <>
        <h1 className="page-title">
          {showCustomerSubscriptions.first_name}{" "}
          {showCustomerSubscriptions.last_name} Subscriptions
        </h1>
        <Button
          text="< Back to customers"
          type="underlined"
          onClicked={() => {
            setShowCustomerSubscriptions(null);
          }}
        />
        <Spacer />
        <ol>
          {showCustomerSubscriptions.subscriptions.map(
            (subscription, subscriptionIndex) => {
              return (
                <li>
                  <div
                    key={subscriptionIndex}
                    className="flex flex-direction-column flex-gap-s"
                  >
                    <h2>{subscription.plan.application.name}</h2>
                    <Spacer height="0" />
                    <h2>Plan</h2>
                    <div>{subscription.plan.name}</div>
                    <div>Duration: {subscription.plan.duration}</div>
                    <div>Cost: {subscription.plan.duration}</div>
                    <div>Renews at: {subscription.renews_at}</div>
                  </div>
                  <Spacer />
                </li>
              );
            }
          )}
        </ol>
      </>
    );
  }

  if (addingNewCustomer === true) {
    return (
      <>
        <h1 className="page-title">Add new customer</h1>
        <Button
          text="< Back to customers"
          type="underlined"
          onClicked={() => {
            setAddingNewCustomer(false);
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={newCustomerData.first_name}
          title="First name"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              first_name: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={newCustomerData.last_name}
          title="Last name"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              last_name: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="email"
          value={newCustomerData.email}
          title="Email"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              email: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="number"
          value={newCustomerData.phone}
          title="Phone"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              phone: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={newCustomerData.country}
          title="Country"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              country: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={newCustomerData.state}
          title="State"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              state: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={newCustomerData.city}
          title="City"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              city: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={newCustomerData.postcode}
          title="Post Code"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              postcode: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={newCustomerData.address}
          title="Address"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              address: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          hasObscureText={true}
          obscureText={true}
          value={newCustomerData.password}
          title="Password"
          onChanged={(value) => {
            console.log(value);
            setNewCustomerData({
              ...newCustomerData,
              password: value,
            });
          }}
        />
        <Spacer />
        <Button
          text="Add"
          icon={<IconPlus />}
          disabled={
            loading ||
            !newCustomerData.first_name ||
            !newCustomerData.last_name ||
            !newCustomerData.email ||
            !newCustomerData.phone ||
            !newCustomerData.country ||
            !newCustomerData.state ||
            !newCustomerData.city ||
            !newCustomerData.postcode ||
            !newCustomerData.address ||
            !newCustomerData.password ||
            (newCustomerData.password && newCustomerData.password.length < 10)
          }
          isLoading={loading}
          onClicked={() => addNewCustomer()}
        />
      </>
    );
  }

  if (customerToEdit !== null) {
    const originalCustomer = customers.find(
      (customer) => customer.uuid === customerToEdit.uuid
    );
    delete originalCustomer.subscriptions;
    return (
      <>
        <h1 className="page-title">
          Edit customer {originalCustomer.first_name}{" "}
          {originalCustomer.last_name}
        </h1>
        <Button
          text="< Back to customers"
          type="underlined"
          onClicked={() => {
            setCustomerToEdit(null);
          }}
        />
        <Spacer height="20px" />
        <FormInput
          type="text"
          value={customerToEdit.first_name}
          title="First name"
          onChanged={(value) => {
            setCustomerToEdit({
              ...customerToEdit,
              first_name: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={customerToEdit.last_name}
          title="Last name"
          onChanged={(value) => {
            setCustomerToEdit({
              ...customerToEdit,
              last_name: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="email"
          value={customerToEdit.email}
          title="Email"
          onChanged={(value) => {
            setCustomerToEdit({
              ...customerToEdit,
              email: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="number"
          value={customerToEdit.phone}
          title="Phone"
          onChanged={(value) => {
            setCustomerToEdit({
              ...customerToEdit,
              phone: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={customerToEdit.country}
          title="Country"
          onChanged={(value) => {
            setCustomerToEdit({
              ...customerToEdit,
              country: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={customerToEdit.state}
          title="State"
          onChanged={(value) => {
            setCustomerToEdit({
              ...customerToEdit,
              state: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={customerToEdit.postcode}
          title="Post Code"
          onChanged={(value) => {
            setCustomerToEdit({
              ...customerToEdit,
              postcode: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={customerToEdit.address}
          title="Address"
          onChanged={(value) => {
            setCustomerToEdit({
              ...customerToEdit,
              address: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={null}
          title="Enter new password"
          hasObscureText={true}
          obsureText={true}
          onChanged={(value) => {
            setCustomerToEdit({
              ...customerToEdit,
              password: value,
            });
          }}
        />
        <Spacer />
        <Button
          text="Update"
          // disabled={!canUpdateMenu}
          isLoading={loading}
          onClicked={() => updateCustomer(originalCustomer)}
        />
      </>
    );
  }

  return (
    <>
      <h2 className="page-title">Customers</h2>
      <div className="flex flex-gap-m flex-align-center">
        <Button
          text="Add new"
          icon={<IconPlus />}
          onClicked={() => setAddingNewCustomer(true)}
        />
        <div className="margin-left-auto">
          <SearchBar
            onUserTyped={(query) => setSearchQuery(query)}
            onUserInputReset={() => getCustomers()}
          />
        </div>
      </div>
      <Spacer />
      <ListPagination
        currentPage={page}
        lastPage={lastPage}
        onPageChanged={(newPage) => setPage(newPage)}
      />
      <List
        headers={[
          "First name",
          "Last name",
          "Email",
          "Phone",
          "Location",
          "Subscriptions",
        ]}
        itemType="customer"
        items={customers}
        isLoading={loading}
        onEdit={(item) => setCustomerToEdit(item)}
        onDelete={(item) => deleteCustomer(item)}
        onShowCustomerSubscriptions={(item) =>
          setShowCustomerSubscriptions(item)
        }
        onAddNewCustomerSubscription={(item) =>
          setAddingNewCustomerSubscription(item)
        }
      />

      {/* Delete menu category verification popup */}
      {deleteCustomerVerifyShow === true && (
        <Popup
          title={`Delete customer ${customerToDelete.first_name} ${customerToDelete.last_name}?`}
          content={
            <>
              <h3>
                <b>Note:</b> This action cannot be reversed. If you are sure
                about it, press the DELETE button below.
              </h3>
              <Spacer />
              <Button
                type="danger"
                text="DELETE"
                onClicked={() => deleteCustomerVerify()}
              />
            </>
          }
          isActive={deleteCustomerVerifyShow}
          onClosed={() => deleteCustomerCancel()}
        />
      )}

      {/* New menu or category added message */}
      {messageType === "success" && (
        <Message
          type="success"
          isInline={false}
          message={message}
          onClosed={() => messageClose()}
        />
      )}
      {messageType === "error" && (
        <Message
          type="error"
          isInline={false}
          message={message}
          onClosed={() => messageClose()}
        />
      )}
    </>
  );
}
